import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, ScrollText } from "lucide-react";

export default function ModuleDetail() {
  const { moduleId } = useParams();
  const [mod, setMod] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    (async () => {
      const [m, p] = await Promise.all([api.get(`/curriculum/${moduleId}`), api.get("/progress")]);
      setMod(m.data);
      setProgress(p.data);
    })();
  }, [moduleId]);

  if (!mod) return <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 label-eyebrow">Loading module…</div>;

  const completedLessons = new Set(progress?.completed_lesson_ids || []);
  const moduleDone = new Set(progress?.completed_module_ids || []).has(mod.id);

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12" data-testid={`module-detail-${mod.order}`}>
      <Link to="/dashboard" className="label-eyebrow inline-flex items-center gap-2 mb-8 hover:text-ink" data-testid="back-to-dashboard">
        <ArrowLeft size={14} strokeWidth={1.5} /> Back to dashboard
      </Link>

      <div className="label-eyebrow mb-3">Module {String(mod.order).padStart(2, "0")} · {mod.domain} · {mod.hours} hours</div>
      <h1 className="font-serif text-5xl text-ink leading-tight mb-6">{mod.title}</h1>
      <p className="text-lg text-muted-foreground leading-relaxed max-w-[65ch] mb-12">{mod.summary}</p>

      <div className="editorial-rule mb-8">Lessons</div>
      <ol className="border-t border-stone">
        {mod.lessons.map((l, i) => {
          const done = completedLessons.has(l.id);
          return (
            <li key={l.id} className="border-b border-stone">
              <Link to={`/lesson/${l.id}`} className="flex items-center justify-between gap-6 py-6 hover:bg-cream px-4 -mx-4 transition-colors group" data-testid={`lesson-link-${l.id}`}>
                <div className="flex items-center gap-6">
                  <div className="font-serif text-3xl text-stone group-hover:text-terracotta transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-serif text-xl text-ink">{l.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{l.minutes} minutes</div>
                  </div>
                </div>
                {done ? <CheckCircle2 size={20} strokeWidth={1.4} className="text-sage" /> : <Circle size={18} strokeWidth={1.4} className="text-stone" />}
              </Link>
            </li>
          );
        })}
      </ol>

      <div className="mt-12 border border-stone bg-cream p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <ScrollText size={24} strokeWidth={1.4} className="text-terracotta mb-3" />
          <div className="label-eyebrow mb-2">Mastery quiz</div>
          <h3 className="font-serif text-2xl text-ink">Demonstrate your understanding</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-[55ch]">
            Each module ends with a short mastery quiz. A score of 80% or higher unlocks the next
            module and counts toward your certificate.
          </p>
        </div>
        <Link to={`/quiz/${mod.quiz_id}`} className="btn-primary shrink-0" data-testid="start-quiz-btn">
          {moduleDone ? "Retake quiz" : "Take mastery quiz"} <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
}
